import React, { useContext } from "react";
import {
	Box,
	Typography,
	Button,
	Paper,
	Divider,
	IconButton,
	getLuminance,
	darken,
	lighten,
	alpha,
} from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { IconArrowLeft } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { CreatorContext } from "../../contexts/CreatorContext.js";
import { UserContext } from "../../contexts/UserContext.js";

function ConfirmedAppointment() {
	const { t } = useTranslation();
	const { state } = useLocation();
	const {
		selectedService,
		selectedOptions,
		selectedWorker,
		selectedDate,
		selectedTime,
	} = state;
	const navigate = useNavigate();
	const { basePath } = useParams();
	const { creator } = useContext(CreatorContext);
	const { user } = useContext(UserContext);

	const getFormattedPriceWithDiscount = (price, discount) => {
		if (discount && discount > 0) {
			const discountedPrice = price - (price * discount) / 100;
			return (
				<>
					{/* Precio original tachado */}
					<span
						style={{
							textDecoration: "line-through",
							color: "gray",
							marginRight: "8px",
						}}
					>
						{new Intl.NumberFormat(creator.language ?? "en", {
							style: "currency",
							currency: creator.currency ?? "eur",
						}).format(price)}
					</span>
					{/* Precio con descuento en rojo */}
					<span style={{ color: "red", fontWeight: "bold" }}>
						{new Intl.NumberFormat(creator.language ?? "en", {
							style: "currency",
							currency: creator.currency ?? "eur",
						}).format(discountedPrice)}
					</span>
				</>
			);
		}

		// Si no hay descuento, devuelve solo el precio normal
		return new Intl.NumberFormat(creator.language ?? "en", {
			style: "currency",
			currency: creator.currency ?? "eur",
		}).format(price);
	};

	return (
		<Box
			sx={{
				padding: "30px",
				height: "100%",
				backgroundColor: creator?.backgroundColor || "#ffffff",
				color: creator?.fontColor || "#000000",
			}}
		>
			<IconButton
				onClick={() => navigate(`/${basePath}/`)} // Navega hacia atrás
				sx={{
					marginBottom: "20px",
					marginLeft: "-8px",
					color: (getLuminance(creator?.backgroundColor ?? "#FFFFFF") >= 0.5
						? darken
						: lighten)(creator?.fontColor ?? "#000000", 0.8),
				}} // Espacio debajo de la flecha
			>
				<IconArrowLeft />
			</IconButton>

			<Box sx={{ maxWidth: 600, margin: "0px auto" }}>
				<Typography
					variant="h4"
					gutterBottom
					sx={{
						fontSize: "18px",
						fontWeight: 600,
						marginBottom: 6,
						textAlign: "center",
					}}
				>
					{t("appointmentConfirmed")}
				</Typography>
				<Paper
					sx={{
						padding: "20px",
						backgroundColor: creator?.backgroundColor ?? "#FFFFFF",
						border: !creator?.showShadows
							? "1px solid " + alpha(creator?.fontColor ?? "#000000", 0.2)
							: "none",
						boxShadow: creator?.showShadows
							? getLuminance(creator?.backgroundColor ?? "#FFFFFF") >= 0.5
								? "0px 2px 10px rgba(0, 0, 0, 0.05)"
								: "0px 2px 10px rgba(255, 255, 255, 0.35)"
							: "none",
						borderRadius: "8px",
						color: creator?.fontColor ?? "#000000",
					}}
				>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							marginBottom: 2,
							flexDirection: "column",
						}}
					>
						<Typography variant="h3" sx={{ fontWeight: 700, fontSize: "20px" }}>
							{selectedTime}
						</Typography>
						<Typography variant="body1" sx={{ textAlign: "right" }}>
							{new Date(selectedDate).toLocaleDateString("es-ES", {
								weekday: "short",
								day: "numeric",
								month: "short",
							})}
						</Typography>
					</Box>

					<Divider
						sx={{
							borderColor: alpha(creator?.fontColor ?? "#000000", 0.1),
							marginY: 2,
						}}
					/>

					<Typography
						variant="h6"
						sx={{ fontWeight: "bold", fontSize: "16px" }}
					>
						{selectedOptions
							? `${selectedService.title} - ${selectedOptions.title}`
							: selectedService.title}
					</Typography>

					<Typography
						variant="body2"
						sx={{ color: "#7f8c8d", marginBottom: 2, fontSize: "14px" }}
					>
						{t("withWorker", { workerName: selectedWorker.name })}
					</Typography>

					<Divider
						sx={{
							borderColor: alpha(creator?.fontColor ?? "#000000", 0.1),
							marginY: 2,
						}}
					/>

					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
						}}
					>
						<Typography
							variant="h6"
							sx={{ fontWeight: "bold", fontSize: "16px" }}
						>
							{t("totalToPay")}
						</Typography>
						<Typography
							variant="h6"
							sx={{ fontWeight: "bold", fontSize: "16px" }}
						>
							{selectedOptions
								? getFormattedPriceWithDiscount(
										selectedOptions.price,
										selectedOptions.discount
								  )
								: getFormattedPriceWithDiscount(
										selectedService.price,
										selectedService.discount
								  )}
						</Typography>
					</Box>
				</Paper>

				<Box
					sx={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
					}}
				>
					<Button
						variant="contained"
						color="primary"
						sx={{
							marginTop: 4,
						}}
						onClick={() =>
							navigate(`/${basePath}${user ? "/appointments" : ""}`)
						}
					>
						{t(user ? "viewAppointments" : "back")}
					</Button>
				</Box>
			</Box>
		</Box>
	);
}

export default ConfirmedAppointment;
